import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import * as actions from '@actions';
import { Alert, Radio, Select, Space, Divider } from 'antd';
import { Form, FormInstance } from 'ALIASED-antd';
import { AnimatePresence, motion } from 'motion/react';
import Grid from '@components/UI/Grid';
import { useArticleWrapped } from '@hooks/useArticleWrapped';
import useUser from '@vl-core/hooks/useUser';
import { useQuery } from '@tanstack/react-query';
import { confirmationModal } from 'vl-common/src/components/Modals';
import styled from '@emotion/styled';
import { getMetaUsersQuery } from 'vl-common/src/schemas/getMetaUsers';
import { useTranslation } from 'react-i18next';
import { useUsersGP } from '@components/Patients/Docman/MaybeGetDocmanConsent';
import SurgeryODSFields from '@components/Patients/Registration/items/SurgeryODSFields';
import { useRuntimeConfig } from 'vl-common/src/hooks/Runtime';
import { RegistrationFormItem } from '../RegistrationFormItem';
import { NHSNumberFormItem } from './NHSNumberFormItem';
import { formatPracticeFromObject } from '@helpers/formatAddress';

// returns true if and only if the user has a GP and that GP accepts Docman
export const usePatientsGPAcceptsDocman = () => {
  const gp = useUsersGP();

  return !!gp?.docman_active;
};

const StyledAlert = styled(Alert)`
  background: transparent;
  border-color: transparent;
`;

export const DocmanConnectivityMessage = ({ gpAcceptsDocman = false }: { gpAcceptsDocman?: boolean }) => {
  const { DOCMAN } = useRuntimeConfig();
  const { t } = useTranslation();

  if (DOCMAN && gpAcceptsDocman)
    return t(
      "Your chosen GP practice is able to receive electronic documents. You'll be able to consent to share your clinical reports digitally with them later on."
    );

  return (
    <StyledAlert
      message={t('Warning')}
      description={t(
        'Please be aware that you are responsible for updating your health records with your GP to ensure the continuity of your care.'
      )}
      type="warning"
      showIcon
    />
  );
};

export const GPFieldset = ({ form, isEditMode = false }: { form: FormInstance; isEditMode?: boolean }) => {
  const dispatch = useDispatch();
  const user = useUser();
  const { t } = useTranslation();
  const isFirstRender = useRef(true);

  const [tempGpType, setTempGpType] = useState<UnknownOrVirtualOrReal>(false);

  const { data: gpDetails } = useQuery({
    enabled: !form.getFieldValue('gp_ods_code'),
    queryKey: ['getGPDetails', user.user_guid],
    queryFn: () => dispatch(actions.getGpDetails(user.user_guid))
  });

  const GP_TYPE_OPTIONS = [
    {
      label: t('I know my GP practice'),
      value: false,
      testid: 'real-gp'
    },
    {
      label: t('I do not know/have a GP practice'),
      value: undefined,
      testid: 'unknown-gp'
    }
  ] as const;

  type UnknownOrVirtualOrReal = (typeof GP_TYPE_OPTIONS)[number]['value'];

  const gpType = Form.useWatch('gp_virtual', { form, preserve: true });
  const setGpType = useCallback(
    (value: UnknownOrVirtualOrReal) => {
      setTempGpType(value);

      // @ts-expect-error
      if (value === true || value === undefined) {
        return form.setFieldsValue({
          gp_virtual: value,
          gp_email: undefined,
          gp_practice_name: undefined,
          gp_postcode: undefined,
          gp_ods_code: undefined,
          gp_summary_consent: undefined,
          nhs_number: undefined,
          gp_investigation_consent: undefined,
          gp_address_preview: undefined
        });
      }

      return form.setFieldsValue({
        gp_virtual: value,
        gp_postcode: gpDetails?.postcode,
        gp_ods_code: gpDetails?.gp_ods_code,
        docman_active: gpDetails?.docman_active,
        gp_address_preview: gpDetails?.gp_ods_code ? formatPracticeFromObject(gpDetails) : '',
        gp_missing_code: undefined
      });
    },
    [form, gpDetails]
  );

  const handleGpTypeChange = useCallback(
    (value: UnknownOrVirtualOrReal) => {
      const hasSomeGpDetails = !!form.getFieldValue('gp_ods_code');

      // @ts-expect-error
      if ((value === true || value === undefined) && hasSomeGpDetails) {
        return confirmationModal({
          title: 'Are you sure?',
          content: 'This action will clear the GP details you have entered.',
          okText: 'Yes, clear GP details',
          closable: true,
          onOk: () => setGpType(value),
          // so that it appears over the DocmanConsentModal
          zIndex: 100000 + 1
        });
      }

      return setGpType(value);
    },
    [form, setGpType]
  );

  const noGPSelectedArticle = useArticleWrapped('CONT_GP_DECLINE');
  const virtualGPSelectedArticle = useArticleWrapped('GP_VIRTUAL_WARNING');

  const { data: userMeta } = useQuery(getMetaUsersQuery());

  const gpSummaryConsent = Form.useWatch('gp_summary_consent', { form, preserve: true });
  const gpInvestigationConsent = Form.useWatch('gp_investigation_consent', { form, preserve: true });
  const gpAllowedConsent = gpSummaryConsent || gpInvestigationConsent;
  const isGpOdsCodeSelected = Form.useWatch('gp_address_preview', { form, preserve: true });
  const odsCode = Form.useWatch('gp_ods_code', { form, preserve: true });
  const docmanActive = Form.useWatch('docman_active', { form, preserve: true });
  const isNHSLoginUser = user?.ident_prov_code === 'NHSLOGIN';

  useEffect(() => {
    if (isEditMode) {
      setGpType(false);
      return;
    }

    if (isFirstRender.current) {
      setGpType(false);
      isFirstRender.current = false;
      return;
    }
    if (gpDetails?.gp_ods_code) setGpType(false);
    // @ts-expect-error: Existing code implies that this property exists, but I
    // haven't seen it.
    if (user.gp_virtual) setGpType(true);
  }, [gpDetails?.gp_ods_code, user, setGpType]);

  return (
    <>
      {!isEditMode && (
        <RegistrationFormItem label={t('Choose the option that applies to you')} labelAlign="left">
          <Radio.Group
            value={tempGpType}
            onChange={(e) => handleGpTypeChange(e.target.value)}
            disabled={isNHSLoginUser}
          >
            <Space direction="vertical">
              {GP_TYPE_OPTIONS.map(({ label, value, testid }) => (
                <Radio
                  key={testid}
                  value={value}
                  data-testid={testid}
                  disabled={!!gpDetails?.gp_ods_code || isEditMode}
                >
                  {label}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </RegistrationFormItem>
      )}

      {gpType === undefined && (
        <>
          {noGPSelectedArticle && (
            <StyledAlert
              message={t('Warning')}
              description={<div dangerouslySetInnerHTML={{ __html: noGPSelectedArticle?.body }} />}
              type="warning"
              showIcon
            />
          )}
          <RegistrationFormItem
            label={t('Reason for no GP details')}
            name="gp_missing_code"
            rules={[{ required: true, message: 'Reason for no GP details is required' }]}
          >
            <Select
              options={userMeta?.gp_missing_reasons?.map((reason) => {
                return { value: reason.gp_missing_code, label: reason.gp_missing_desc };
              })}
              data-testid="gp_not_selected_reason"
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
            />
          </RegistrationFormItem>
        </>
      )}

      {gpType === true && virtualGPSelectedArticle && (
        <StyledAlert
          message={t('Warning')}
          description={<div dangerouslySetInnerHTML={{ __html: virtualGPSelectedArticle?.body }} />}
          type="warning"
          showIcon
        />
      )}

      <AnimatePresence>
        {!gpType && gpType !== undefined && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.1 }}
          >
            <Grid columns={1}>
              <fieldset aria-labelledby="gpPractice">
                <div id="gpPractice">
                  <h2 style={{ padding: 0, margin: 0 }}>{t('Select your GP practice')}</h2>
                </div>
                <Divider style={{ marginTop: 0, marginBottom: '1em' }} aria-hidden="true" />
                <SurgeryODSFields
                  form={form}
                  FormItemComponent={RegistrationFormItem}
                  searchFieldName="gp_postcode"
                  previewFieldName="gp_address_preview"
                  odsCodeFieldName="gp_ods_code"
                  // Do not set initial search value otherwise patient flow is confusing
                  // initialSearchValue={form.getFieldValue('postcode') || user.postcode}
                  isConsentGiven={gpAllowedConsent}
                  isGPFormVisible={!gpType && gpType !== undefined}
                  disabled={
                    (!!gpDetails?.gp_ods_code || isNHSLoginUser || !!gpDetails?.ro_fields?.includes('gp_ods_code')) &&
                    !isEditMode
                  }
                />
              </fieldset>

              {odsCode && <DocmanConnectivityMessage gpAcceptsDocman={docmanActive} />}
              {!!(isGpOdsCodeSelected && gpAllowedConsent) && (
                <NHSNumberFormItem
                  FormItemComponent={RegistrationFormItem}
                  form={form}
                  nhsNumberFieldName="nhs_number"
                  membershipFieldName="mem_no"
                  disabled={isNHSLoginUser}
                />
              )}
            </Grid>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
